import * as React from "react"
import {
  DateLib,
  DayPicker,
  Root as DayPickerRoot,
  useDayPicker,
  type DayPickerProps,
  type RootProps,
} from "react-day-picker"

import { ChevronLeft, ChevronRight } from "lucide-react"

import { cn } from "../../cn"
import { Tooltip } from "../Tooltip/Tooltip"
import {
  DateRangeCalendarContextProvider,
  useDateRangeCalendarContext,
} from "./DateRangeCalendarContext"

/* ── Variants ─────────────────────────────────────────────── */

const dayBaseClasses = [
  "relative p-0 text-center text-sm",
  "focus-within:relative focus-within:z-20",
]

const dayButtonClasses = [
  "inline-flex size-8 cursor-pointer items-center justify-center rounded-md",
  "text-sm font-normal text-coval-text tabular-nums",
  "transition-colors duration-150",
  "hover:bg-surface-1 hover:text-coval-text",
  "disabled:pointer-events-none disabled:opacity-40",
]

/**
 * Selection is painted on the day cell rather than the button so a range reads
 * as one continuous band: the cell carries the fill, the button the rounding.
 */
const calendarClassNames = {
  root: "w-fit",
  months: "relative flex flex-col gap-4 sm:flex-row",
  month: "flex flex-col gap-4",
  month_caption: "flex h-8 items-center justify-center",
  caption_label: "text-sm font-medium text-coval-text",
  nav: "absolute inset-x-0 top-0 flex h-8 items-center justify-between px-1",
  button_previous: cn(
    "inline-flex size-7 cursor-pointer items-center justify-center rounded-md",
    "text-coval-text-muted transition-colors duration-150",
    "hover:bg-surface-1 hover:text-coval-text",
    "disabled:pointer-events-none disabled:opacity-40",
  ),
  button_next: cn(
    "inline-flex size-7 cursor-pointer items-center justify-center rounded-md",
    "text-coval-text-muted transition-colors duration-150",
    "hover:bg-surface-1 hover:text-coval-text",
    "disabled:pointer-events-none disabled:opacity-40",
  ),
  chevron: "size-4",
  month_grid: "w-full border-collapse",
  weekdays: "flex",
  weekday: "w-8 text-xs font-normal text-coval-text-muted",
  week: "mt-1 flex w-full",
  day: cn(dayBaseClasses),
  day_button: cn(dayButtonClasses),
  today: "font-semibold text-coval-text underline underline-offset-4",
  outside: "text-coval-text-muted",
  disabled: "opacity-40",
  hidden: "invisible",
  selected: "",
  range_start: "rounded-l-md bg-surface-selected",
  range_middle: "bg-surface-selected",
  range_end: "rounded-r-md bg-surface-selected",
  dropdowns: "flex items-center gap-2",
  months_dropdown: "rounded-md bg-surface-0 px-2 py-1 text-sm text-coval-text",
  years_dropdown: "rounded-md bg-surface-0 px-2 py-1 text-sm text-coval-text",
} satisfies DayPickerProps["classNames"]

/**
 * Names a day button with the day as a cardinal number ("September 1, 2026"),
 * so the name contains the digit the button shows. react-day-picker's default
 * writes an ordinal ("September 1st"), which voice control cannot match.
 */
const labelDayButton: NonNullable<
  NonNullable<DayPickerProps["labels"]>["labelDayButton"]
> = (date, modifiers, options, dateLib) => {
  let label = (dateLib ?? new DateLib(options)).format(date, "EEEE, MMMM d, y")
  if (modifiers.today) {
    label = `Today, ${label}`
  }
  if (modifiers.selected) {
    label = `${label}, selected`
  }
  return label
}

/* ── Component ────────────────────────────────────────────── */

export type CalendarProps = DayPickerProps

const RootComponentContext =
  React.createContext<React.ComponentType<RootProps>>(DayPickerRoot)

/**
 * Month grid for picking a day or a range, styled on the design tokens.
 *
 * Wraps `react-day-picker`, so every selection mode and modifier it supports
 * passes straight through; only the presentation is ours.
 */
function Calendar({
  className,
  classNames,
  components,
  labels,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  const calendarContext = useDateRangeCalendarContext()

  const handleMonthChange = (month: Date) => {
    calendarContext?.onMonthChange?.(month)
    props.onMonthChange?.(month)
  }

  const calendar = (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        ...calendarClassNames,
        ...(calendarContext?.selectionColor === "blue" && {
          selected: "bg-coval-calendar-selection",
          range_start: "rounded-l-md bg-coval-calendar-selection",
          range_middle: "bg-coval-calendar-selection",
          range_end: "rounded-r-md bg-coval-calendar-selection",
        }),
        ...classNames,
      }}
      components={{
        Chevron: CalendarChevron,
        DayButton: CalendarDayButton,
        ...components,
        ...(calendarContext && { Root: CalendarRoot }),
      }}
      labels={{ labelDayButton, ...labels }}
      {...props}
      timeZone={props.timeZone ?? calendarContext?.timeZone}
      onMonthChange={calendarContext ? handleMonthChange : props.onMonthChange}
    />
  )

  return calendarContext ? (
    <RootComponentContext.Provider value={components?.Root ?? DayPickerRoot}>
      {calendar}
    </RootComponentContext.Provider>
  ) : (
    calendar
  )
}

Calendar.displayName = "Calendar"

function CalendarRoot(props: RootProps) {
  const Root = React.useContext(RootComponentContext)
  const { months, dayPickerProps } = useDayPicker()
  const onVisibleGridChange = useDateRangeCalendarContext()?.onVisibleGridChange
  const timeZone =
    dayPickerProps.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone
  const reportedGrid = React.useRef<string | undefined>(undefined)
  let startDate: string | undefined
  let lastDate: string | undefined
  for (const month of months) {
    for (const week of month.weeks) {
      for (const day of week.days) {
        if (!startDate || day.isoDate < startDate) {
          startDate = day.isoDate
        }
        if (!lastDate || day.isoDate > lastDate) {
          lastDate = day.isoDate
        }
      }
    }
  }
  const end = lastDate ? new Date(`${lastDate}T12:00:00Z`) : undefined
  end?.setUTCDate(end.getUTCDate() + 1)
  const endDate = end?.toISOString().slice(0, 10)

  React.useEffect(() => {
    if (!startDate || !endDate || !onVisibleGridChange) {
      return
    }
    const key = `${startDate}/${endDate}/${timeZone}`
    if (reportedGrid.current === key) {
      return
    }
    reportedGrid.current = key
    onVisibleGridChange({ startDate, endDate, timeZone })
  }, [endDate, onVisibleGridChange, startDate, timeZone])

  return <Root {...props} />
}

function CalendarChevron({
  className,
  orientation,
}: {
  className?: string
  orientation?: "left" | "right" | "up" | "down"
}) {
  const Icon = orientation === "left" ? ChevronLeft : ChevronRight
  return <Icon className={className} aria-hidden />
}

/**
 * The selected day reads as filled, and the ends of a range keep that fill
 * while the days between them take the softer band from the cell.
 */
function CalendarDayButton({
  className,
  modifiers,
  day: _day,
  ...props
}: React.ComponentProps<
  NonNullable<NonNullable<DayPickerProps["components"]>["DayButton"]>
>) {
  const calendarContext = useDateRangeCalendarContext()
  // react-day-picker moves keyboard focus by flagging the target day as
  // `focused`; its own DayButton then focuses itself. A replacement DayButton
  // has to do the same, or arrow keys, Home/End and PageUp/PageDown leave
  // focus where it was.
  const ref = React.useRef<HTMLButtonElement>(null)
  React.useEffect(() => {
    if (modifiers.focused) {
      ref.current?.focus()
    }
  }, [modifiers.focused])
  const isRangeMiddle = modifiers.range_middle
  const isFilled = modifiers.selected && !isRangeMiddle
  const hasBlueSelection = calendarContext?.selectionColor === "blue"
  const dateKey = _day?.isoDate
  const count = dateKey ? calendarContext?.density.get(dateKey) : undefined
  const densityLabel =
    calendarContext?.densityUnit === "conversations"
      ? "conversations"
      : calendarContext?.densityUnit === "reviews"
        ? "completed reviews"
        : "triage labels"
  const densityClass =
    (hasBlueSelection || (!modifiers.selected && !isRangeMiddle)) &&
    count !== undefined
      ? densityClassForCount(count, calendarContext?.maximumDensity ?? 0)
      : undefined
  const button = (
    <button
      ref={ref}
      type="button"
      {...props}
      className={cn(
        className,
        // Outside-month days take the muted text only on the plain surface,
        // where it holds 4.5:1; on a density or selection fill they keep the
        // text color that fill was chosen against.
        modifiers.outside &&
          !densityClass &&
          !modifiers.selected &&
          !isRangeMiddle &&
          "text-coval-text-muted",
        densityClass,
        densityClass && hasBlueSelection && "m-0.5 size-7",
        hasBlueSelection &&
          modifiers.selected &&
          "text-coval-calendar-selection-foreground",
        isFilled &&
          (hasBlueSelection
            ? "font-medium"
            : "bg-coval-contrast font-medium text-coval-text-on-contrast hover:bg-coval-contrast-hover hover:text-coval-text-on-contrast"),
        isRangeMiddle && !densityClass && "rounded-none",
      )}
      aria-label={
        count !== undefined
          ? `${props["aria-label"] ?? ""}, ${count} ${densityLabel}`.replace(
              /^, /,
              "",
            )
          : props["aria-label"]
      }
    />
  )

  if (count === undefined || !calendarContext?.densityUnit) {
    return button
  }

  return (
    <Tooltip
      content={`${count} ${densityLabel}${calendarContext.densityHelperText ? `. ${calendarContext.densityHelperText}` : ""}`}
    >
      {button}
    </Tooltip>
  )
}

function densityClassForCount(count: number, maximum: number) {
  if (count <= 0 || maximum <= 0) {
    return undefined
  }
  const relativeCount = count / maximum
  if (relativeCount <= 0.25) {
    return "bg-coval-calendar-density-very-light"
  }
  if (relativeCount <= 0.5) {
    return "bg-coval-calendar-density-light"
  }
  if (relativeCount <= 0.75) {
    return "bg-coval-calendar-density-medium"
  }
  return "bg-coval-calendar-density-strong"
}

export { Calendar, calendarClassNames, DateRangeCalendarContextProvider }
export type {
  DateRangeCalendarContextValue,
  DateRangeCalendarDensityUnit,
  DateRangeCalendarStatus,
  DateRangeCalendarVisibleGrid,
} from "./DateRangeCalendarContext"
